import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, Alert, IconButton , Tooltip, CircularProgress, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { useLocation, useParams, Link} from "react-router-dom";
import Grid from "@mui/material/Grid2";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { messages } from "../config/messages";
import {USER_ACTIONS, isAllowedAction} from "../config/auth";
import { useKeycloak } from "@react-keycloak/web";
import { PARTICIPANT_DOCUMENTS, PARTICIPANT_RETRIEVE_ACTUAL_DOCUMENT } from "../config/settings";

const DocumentUploadSection = () => {
    const location = useLocation();
    const { id } = useParams();
    const { keycloak } = useKeycloak();
    const [isEditMode, setIsEditMode] = useState(false); // To track edit mode
    const [participantId, setParticipantId] = useState(null); // For the PUT request
    const [documents, setDocuments] = useState([{ title: "", description: "", file: null, id: null }]);
    const [openDialog, setOpenDialog] = useState(false);
    const [docToRemoveIndex, setDocToRemoveIndex] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState({});
    const [uploadErrors, setUploadErrors] = useState({});
    const [selectedFiles, setSelectedFiles] = useState({}); // Track new file selection
    const [uploading, setUploading] = useState({});
    const maxSize = 20;

    useEffect(() => {
        if (id) {
            setIsEditMode(true);
            setParticipantId(id);
        }
    }, [id]);

    useEffect(() => {
        if (isEditMode && participantId) {
            fetchDocuments();
        }
    }, [isEditMode, participantId]);

    const fetchDocuments = async () => {
        try {
            const res = await axios.get(PARTICIPANT_DOCUMENTS, {
                params: { participant: participantId }
            });
            if (Array.isArray(res.data)) {
                const filteredDocs = res.data;
                if (filteredDocs.length > 0) {
                    setDocuments(filteredDocs);
                } else {
                    setDocuments([{ title: "", description: "", file: null, id: null }]);
                }
            }
        } catch (err) {
            console.error("Error fetching documents:", err);
        }
    };

    const handleAddDocument = () => {
        setDocuments([...documents, { title: "", description: "", file: null }]);
    };

    const handleChange = (index, field, value) => {
        const newDocs = [...documents];
        newDocs[index][field] = value;
        setDocuments(newDocs);
        // Track when a new file is selected
        if (field === "file") {
            setSelectedFiles(prev => ({ ...prev, [index]: true }));
            setUploadErrors(prev => ({ ...prev, [index]: null }));
            setUploadSuccess(prev => ({ ...prev, [index]: false }));
        }
    };

    const handleRemove = (index) => {
        const doc = documents[index];
        const hasFile =
            (typeof doc.file === "string" && doc.file.trim() !== "") ||
            doc.file instanceof File;

        if (hasFile) {
            setDocToRemoveIndex(index);
            setOpenDialog(true);
        } else {
            const newDocs = [...documents];
            newDocs.splice(index, 1);
            setDocuments(newDocs.length ? newDocs : [{ title: "", description: "", file: null, id: null }]);
        }
    };

    const confirmRemoveFile = async (index) => {
        const doc = documents[index];
        if (!doc?.id) {
          // It's a new, unsaved document – just remove from state
          const newDocs = [...documents];
          newDocs.splice(index, 1);
          setDocuments(newDocs.length ? newDocs : [{ title: "", description: "", file: null, id: null }]);
          return;
        }
        try {
          const formData = new FormData();
          formData.append("title", "");
          formData.append("description", "");
          formData.append("file", "");
          // formData.append("participant", participantId); // which participant
          await axios.delete(`${PARTICIPANT_DOCUMENTS}${doc.id}/`, formData);

          await fetchDocuments();
        } catch (error) {
          console.error("Failed to remove file", error);
        }
      };


    const handleUpload = async (index) => {
        const doc = documents[index];
        const docId = doc.id;
        const formData = new FormData();
        formData.append("title", doc.title);
        formData.append("description", doc.description);
        if (doc.file instanceof File) formData.append("file", doc.file);
        setUploading(prev => ({ ...prev, [index]: true }));

        try {
            let res;
            if (isEditMode && doc.id) {
                // Update existing document
                formData.append("participant", participantId);
                res = await axios.put(`${PARTICIPANT_DOCUMENTS}${docId}/`, formData);
                const updatedDocs = [...documents];
                updatedDocs[index] = res.data;
                setDocuments(updatedDocs);
                setSelectedFiles(prev => ({ ...prev, [index]: false }));
            } else {
                // Upload new document
                formData.append("participant", participantId);
                await axios.post(PARTICIPANT_DOCUMENTS, formData);
                await fetchDocuments();
                setUploadSuccess((prev) => ({ ...prev, [index]: true }));
                setUploadErrors((prev) => ({ ...prev, [index]: null }));

        setTimeout(() => {
            setUploadSuccess((prev) => ({ ...prev, [index]: false }));
        }, 3000); // optional reset of green state after success
            }
        } catch (err) {
            console.error("Upload error", err);
            setUploadSuccess((prev) => ({ ...prev, [index]: false }));
            setUploadErrors((prev) => ({
                ...prev,
                [index]: err.response?.data?.detail || "Upload failed. Please check the file type or try again.",
            }));
        } finally {
            setUploading(prev => ({ ...prev, [index]: false }));
        }
    };

    const handleDownload = async (url, fileName) => {
        try {
            const res = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${keycloak.token}`,
                },
            });
            if (!res.ok) throw new Error("Failed to download");

            const blob = await res.blob();
            const downloadLink = document.createElement("a");
            downloadLink.href = window.URL.createObjectURL(blob);
            downloadLink.download = fileName;
            downloadLink.click();
        } catch (err) {
            console.error("Download failed:", err);
            alert("Failed to download file.");
        }
    };

    const renderFileSection = (doc, index) => {
        if (typeof doc.file === "string") {
            const fileName = decodeURIComponent(doc.file.split("/").pop());
            const downloadUrl = PARTICIPANT_RETRIEVE_ACTUAL_DOCUMENT+ "participant_"+`${participantId}/`+`${fileName}`;
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Tooltip title={messages.documentDownloadButtontTooltip}><IconButton color="secondary" aria-label="download">
                        <DownloadIcon onClick={() => handleDownload(downloadUrl, fileName)}/>
                    </IconButton></Tooltip>
                    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                        {fileName.length > 40 ? `${fileName.slice(0, 37)}...` : fileName}
                    </Typography>
                    <Tooltip title={messages.updateSelectDocumentTooltipButton}>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadFileIcon />}
                        >
                            {messages.updateSelectDocumentButton}
                            <input
                                type="file"
                                accept=".pdf, .png, .jpeg, .jpg"
                                hidden
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file && file.size > maxSize * 1024 * 1024) {
                                        alert(`${messages.uploadFileSizeWarning} ${maxSize}MB`);
                                        return;
                                    }
                                    handleChange(index, "file", file);
                                }}
                            />
                        </Button>
                    </Tooltip>
                </Box>
            );
        }

        return (
            <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
            >
                {doc.file ? doc.file.name : messages.documentPermitedFiles}
                <input
                    type="file"
                    accept=".pdf, .png, .jpeg, .jpg"
                    hidden
                    onChange={(e) => handleChange(index, "file", e.target.files[0])}
                />
            </Button>
        );
    };

    return (
        <Box sx={{ m: 2 }}>

            <fieldset>
                <legend><Typography variant="h4">{messages.documentUploadAreaTitle}</Typography> </legend>
                <Stack spacing={2}>
                    {documents.map((doc, index) => (
                        <Box key={index} sx={{ p: 2 }}>
                            <Typography variant="h5" sx={{ color: 'text.secondary', mb: 2 }}>
                                {doc.file
                                    ? messages.ducumentTopHelpUpdateDocumentText
                                    : messages.ducumentTopHelpNewDocumentText}
                            </Typography>
                            <Grid container direction="row" spacing={2}>
                                {/* Left: Title + Description */}
                                <Grid size={8}>
                                    <Stack spacing={2}>
                                        <TextField
                                            label={messages.docTitleFieldLabel}
                                            helperText={messages.docTitleFieldHelpText}
                                            value={doc.title}
                                            onChange={(e) => handleChange(index, "title", e.target.value)}
                                            fullWidth
                                            variant="filled"
                                            required
                                        />
                                        <TextField
                                            label={messages.docDescriptionFieldLabel}
                                            value={doc.description}
                                            helperText={messages.docDescriptionFieldHelpText}
                                            onChange={(e) => handleChange(index, "description", e.target.value)}
                                            fullWidth
                                            multiline
                                            required
                                            rows={2}
                                            variant="filled"
                                        />
                                    </Stack>
                                </Grid>

                                {/* Right: File upload & button */}
                                <Grid item size={4}>
                                    <Stack spacing={2}>
                                        {renderFileSection(doc, index)}
                                        {(selectedFiles[index] || !doc.id) && (<>
                                            <Button
                                                onClick={() => handleUpload(index)}
                                                variant="outlined"
                                                color={doc.id ? "secondary" : "primary"}
                                                disabled={!doc.title || !doc.description}
                                                startIcon={
                                                    uploading[index] ? (
                                                        <CircularProgress size={18} color="inherit" />
                                                    ) : undefined
                                                }
                                            >
                                                {doc.id ? messages.updateDocumentButton : messages.uploadDocumentButton}
                                            </Button>
                                            {(!doc.title || !doc.description) ?
                                                <Typography variant="body2" sx={{fontWeight: 600, textAlign: "center"}}>{messages.updateDocumentRequiredAfterButtonMessage}</Typography> :
                                                <span></span>}
                                        </>)}
                                        {uploadErrors[index] && (
                                            <Alert severity="error">
                                                {uploadErrors[index]}
                                            </Alert>
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>

                            {/* Bottom: Add/Remove Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                                <Tooltip title={messages.addDocumentButton}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddCircleIcon />}
                                        onClick={handleAddDocument}
                                    >
                                        {messages.addDocumentButton}
                                    </Button>
                                </Tooltip>

                                {(documents.length > 1 || doc.id) && (
                                    <Tooltip title={messages.removeDocumentButton}>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<RemoveCircleIcon />}
                                            onClick={() => handleRemove(index)}
                                        >
                                            {messages.removeDocumentButton}
                                        </Button>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </fieldset>

            {(isAllowedAction(USER_ACTIONS.ADMINISTER_PARTICIPANT_REGISTRY) || isAllowedAction(USER_ACTIONS.VIEW_PARTICIPANT_REGISTRY)) && <Box sx={{p: 2, display: "flex", justifyContent:"flex-end"}}>
                <Button variant="contained" component={Link} to="/participant-list" >{messages.backToParticipantListButtonLabel}</Button>
            </Box>}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
            >
                <DialogTitle>{messages.removeDocumentDialogTitle}</DialogTitle>
                <DialogContent>
                    <Typography>{messages.removeDocumentDialogAsk}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} color="secondary">
                        {messages.cancel}
                    </Button>
                    <Button
                        onClick={async () => {
                            await confirmRemoveFile(docToRemoveIndex);
                            setOpenDialog(false);
                        }}
                        color="error"
                    >
                        {messages.removeDocumentDialogConfirm}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DocumentUploadSection;
